// シフト決定画面の期間ナビゲーション（前後・月／週／日・リスト／カレンダー・入力状況トグル）の描画
// index.blade.php の [grid-area:nav] と同じマークアップを出す。リンクには data-js-nav を付け、クリックを JS が横取りする

import { buildUrl, classNames, esc } from './html'

const LINK_BASE = 'flex items-center gap-x-1 px-3 py-1 border bg-white hover:bg-white/50 transition-color duration-150'
const ACTIVE_BORDER = 'border-indigo-600 hover:border-indigo-600/50'
const INACTIVE_BORDER = 'border-gray-300 hover:border-gray-300/50'

/**
 * セグメント型のリンク1つ
 *
 * @param {string} href
 * @param {string} icon Material Symbols のアイコン名
 * @param {string} label
 * @param {boolean} active
 * @param {string} extraClasses 角丸や隣接ボーダーの調整
 * @returns {string}
 */
const segmentLink = (href, icon, label, active, extraClasses) => `
    <a href="${esc(href)}" data-js-nav class="${classNames(LINK_BASE, active ? ACTIVE_BORDER : INACTIVE_BORDER, extraClasses)}">
        <span class="material-symbols-outlined text-base text-indigo-600">${esc(icon)}</span>
        <span class="text-sm text-indigo-600 whitespace-nowrap">${esc(label)}</span>
    </a>`

/**
 * 月／週／日表示の切替
 *
 * @param {object} period API の period
 * @param {string} indexUrl
 * @returns {string}
 */
const periodTypeLinks = (period, indexUrl) => {
    const type = period.type

    return `
    <div class="flex flex-row items-center">
        ${segmentLink(buildUrl(indexUrl, period.month_query), 'calendar_view_month', '月表示', type === 'month', classNames('rounded-l', (type === 'week' || type === 'day') && 'border-r-0'))}
        ${segmentLink(buildUrl(indexUrl, period.week_query), 'calendar_view_week', '週表示', type === 'week', classNames(type === 'month' && 'border-l-0', type === 'day' && 'border-r-0'))}
        ${segmentLink(buildUrl(indexUrl, period.day_query), 'calendar_view_day', '日表示', type === 'day', classNames('rounded-r', (type === 'month' || type === 'week') && 'border-l-0'))}
    </div>`
}

/**
 * リスト／カレンダー表示の切替（日表示のときだけ）
 *
 * @param {object} display API の display
 * @param {string} indexUrl
 * @returns {string}
 */
const displayModeLinks = (display, indexUrl) => `
    <div class="flex flex-row items-center">
        ${segmentLink(buildUrl(indexUrl, display.list_query), 'lists', 'リスト表示', display.mode === 'list', classNames('rounded-l', display.mode !== 'list' && 'border-r-0'))}
        ${segmentLink(buildUrl(indexUrl, display.calendar_query), 'calendar_today', 'カレンダー表示', display.mode === 'calendar', classNames('rounded-r', display.mode !== 'calendar' && 'border-l-0'))}
    </div>`

/**
 * 講師の入力状況（〇✕列）のトグルスイッチ（カレンダー表示では出さない）
 *
 * @param {object} display API の display
 * @param {string} indexUrl
 * @returns {string}
 */
const availabilityToggle = (display, indexUrl) => {
    const on = display.shows_availability

    return `
    <a href="${esc(buildUrl(indexUrl, display.availability_toggle_query))}" data-js-nav class="inline-flex items-center cursor-pointer gap-x-2" title="${on ? '講師の入力状況の列を非表示にする' : '講師の入力状況の列を表示する'}">
        <span class="relative inline-block w-10 h-5 rounded-full transition ${on ? 'bg-indigo-600' : 'bg-gray-300'}">
            <span class="absolute top-0 left-0 w-4 h-4 bg-white rounded-full transition" style="margin: 2px;${on ? ' transform: translateX(20px);' : ''}"></span>
        </span>
        <span class="text-sm whitespace-nowrap">講師の入力状況を表示（${on ? 'ON' : 'OFF'}）</span>
    </a>`
}

/**
 * 前へ／次へのボタン
 *
 * @param {string} href
 * @param {string} label
 * @param {'back'|'forward'} direction
 * @returns {string}
 */
const stepButton = (href, label, direction) => {
    const icon = `<span class="material-symbols-outlined text-lg leading-none">arrow_${direction}</span>`
    const text = `<span class="[text-box:trim-both_cap_alphabetic] text-small whitespace-nowrap">${esc(label)}</span>`

    return `
    <a href="${esc(href)}" data-js-nav>
        <button class="admin-indigo-outline-button flex items-center gap-x-2" type="button">${direction === 'back' ? icon + text : text + icon}</button>
    </a>`
}

/**
 * ナビゲーション全体
 *
 * @param {object} data API のレスポンス
 * @param {{indexUrl: string}} context
 * @returns {string}
 */
export const renderNav = (data, { indexUrl }) => {
    const { period, display } = data

    return `
    <div class="[grid-area:nav] flex flex-nowrap items-end justify-between text-center backdrop-blur-sm z-50 js-decision-nav">
        ${stepButton(buildUrl(indexUrl, period.previous.query), period.previous.label, 'back')}
        <div class="flex flex-col gap-y-2">
            <p class="font-bold text-xl">${esc(period.label)} 対面指導シフト</p>
            <div class="flex flex-wrap justify-center gap-x-4 gap-y-2">
                ${periodTypeLinks(period, indexUrl)}
                ${period.type === 'day' ? displayModeLinks(display, indexUrl) : ''}
                ${display.mode !== 'calendar' ? availabilityToggle(display, indexUrl) : ''}
            </div>
        </div>
        ${stepButton(buildUrl(indexUrl, period.next.query), period.next.label, 'forward')}
    </div>`
}
